import { Box, Chip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import { getWorkflowTemplate } from '../../config/workflowTemplates';
import Sidebar from '../Sidebar';
import ActivityTicker from './components/ActivityTicker';
import FlowCanvas from './components/FlowCanvas';
import LoadingScreen from './components/LoadingScreen';
import ModalsContainer from './components/ModalsContainer';
import { useFlowDiagram } from './hooks/useFlowDiagram';

const SIDEBAR_WIDTH_EXPANDED = 158;
const SIDEBAR_WIDTH_COLLAPSED = 52;
const SIDEBAR_COLLAPSED_KEY = 'cloud-canvas-sidebar-collapsed';

const readCollapsedPreference = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const Canvas = () => {
  const { projectId } = useParams();
  const template = getWorkflowTemplate(projectId);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPreference);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        sidebarCollapsed ? '1' : '0'
      );
    } catch {
      // Ignore private-mode / blocked storage.
    }
  }, [sidebarCollapsed]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onEdgesDelete,
    onConnect,
    onConnectStart,
    onConnectEnd,
    onConnectCheck,
    onInit,
    onDrop,
    onDragOver,
    isLoading,
    error,
    handleDeploy,
    deployRaw,
    reactFlowWrapper,
    // handleNameSubmit,
    modalState,
    setModalState,
    handleSubmitForm,
    missingConnections,
  } = useFlowDiagram();

  const flowCanvasProps = useMemo(
    () => ({
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onEdgesDelete,
      onConnect,
      onConnectStart,
      onConnectEnd,
      onConnectCheck,
      onInit,
      onDrop,
      onDragOver,
      reactFlowWrapper,
    }),
    [
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      onEdgesDelete,
      onConnect,
      onConnectStart,
      onConnectEnd,
      onConnectCheck,
      onInit,
      onDrop,
      onDragOver,
      reactFlowWrapper,
    ]
  );

  const modalsContainerProps = useMemo(
    () => ({
      modalState,
      setModalState,
      onSubmitForm: handleSubmitForm,
      // handleNameSubmit,
      missingConnections,
      onDeploy: handleDeploy,
      onDeployConfirmed: deployRaw,
      nodes,
    }),
    [
      modalState,
      setModalState,
      handleSubmitForm,
      // handleNameSubmit,
      missingConnections,
      handleDeploy,
      deployRaw,
      nodes,
    ]
  );

  if (isLoading) return <LoadingScreen />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)', width: '100%' }}>
      <Box
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          minWidth: 0,
          transition: 'width 0.2s ease',
          overflow: 'hidden',
        }}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />
      </Box>
      <ReactFlowProvider>
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: 0, position: 'relative' }}>
          {template && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                bgcolor: 'rgba(18,22,42,0.78)',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="subtitle2">{template.name}</Typography>
              {(template.patterns || []).slice(0, 3).map((p) => (
                <Chip
                  key={p}
                  size="small"
                  label={p}
                  sx={{ height: 20, color: '#fff', borderColor: 'rgba(255,255,255,0.35)' }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
          <FlowCanvas {...flowCanvasProps} />
          <ActivityTicker nodes={nodes} />
        </Box>
        <ModalsContainer {...modalsContainerProps} />
      </ReactFlowProvider>
    </Box>
  );
};

export default Canvas;
